import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { FactoryIcon, AlertTriangleIcon, ZapIcon, ActivityIcon, MapPinIcon, GaugeIcon, MapIcon, ListIcon } from 'lucide-react';
const substationTableData = [{
  name: 'Kempton Park Main',
  region: 'North',
  capacity: '120 MVA',
  load: 85,
  status: 'Warning',
  transformers: 8,
  lat: -26.1,
  lng: 28.23
}, {
  name: 'Boksburg Central',
  region: 'East',
  capacity: '80 MVA',
  load: 62,
  status: 'Normal',
  transformers: 5,
  lat: -26.21,
  lng: 28.25
}, {
  name: 'Germiston South',
  region: 'South',
  capacity: '150 MVA',
  load: 91,
  status: 'Critical',
  transformers: 10,
  lat: -26.22,
  lng: 28.17
}, {
  name: 'Benoni East',
  region: 'East',
  capacity: '60 MVA',
  load: 45,
  status: 'Normal',
  transformers: 4,
  lat: -26.19,
  lng: 28.32
}, {
  name: 'Edenvale North',
  region: 'North',
  capacity: '90 MVA',
  load: 78,
  status: 'Normal',
  transformers: 6,
  lat: -26.14,
  lng: 28.16
}, {
  name: 'Springs West',
  region: 'East',
  capacity: '100 MVA',
  load: 88,
  status: 'Warning',
  transformers: 7,
  lat: -26.25,
  lng: 28.44
}, {
  name: 'Tembisa Hub',
  region: 'North',
  capacity: '110 MVA',
  load: 72,
  status: 'Normal',
  transformers: 8,
  lat: -25.99,
  lng: 28.23
}, {
  name: 'Alberton South',
  region: 'South',
  capacity: '70 MVA',
  load: 55,
  status: 'Normal',
  transformers: 5,
  lat: -26.27,
  lng: 28.13
}, {
  name: 'Brakpan Central',
  region: 'East',
  capacity: '85 MVA',
  load: 94,
  status: 'Critical',
  transformers: 6,
  lat: -26.24,
  lng: 28.37
}, {
  name: 'Nigel Industrial',
  region: 'South-East',
  capacity: '95 MVA',
  load: 67,
  status: 'Normal',
  transformers: 7,
  lat: -26.43,
  lng: 28.47
}];
const substationChartData = substationTableData.map(s => ({
  name: s.name.split(' ')[0],
  load: s.load
}));
const getStatusColor = (status: string) => {
  switch (status) {
    case 'Normal':
      return '#8DB43E';
    case 'Warning':
      return '#E07020';
    case 'Critical':
      return '#ef4444';
    default:
      return '#9ca3af';
  }
};
const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'Normal':
      return 'bg-green-100 text-green-800';
    case 'Warning':
      return 'bg-orange-100 text-orange-800';
    case 'Critical':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
const getLoadColor = (load: number) => {
  if (load >= 85) return 'text-red-600';
  if (load >= 70) return 'text-orange-600';
  return 'text-mavis-green';
};
const getLoadBarColor = (load: number) => {
  if (load >= 85) return 'bg-red-500';
  if (load >= 70) return 'bg-orange-500';
  return 'bg-mavis-green';
};
export function SubstationMonitoring() {
  const [view, setView] = useState<'map' | 'list'>('map');
  return <div className="p-8 max-w-7xl mx-auto animate-in fade-in duration-500" data-id="element-2475">
      <div className="mb-8" data-id="element-2476">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2477">
          Substation Monitoring
        </h1>
        <p className="text-gray-500" data-id="element-2478">
          Monitor substation and transformer loads across the municipal regional
          area.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-2479">
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-2480">
          <div className="p-3 bg-mavis-teal/10 text-mavis-teal rounded-none" data-id="element-2481">
            <FactoryIcon className="w-5 h-5" data-id="element-2482" />
          </div>
          <div data-id="element-2483">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-2484">
              Total Substations
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-2485">42</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-2486">
          <div className="p-3 bg-red-50 text-red-600 rounded-none" data-id="element-2487">
            <AlertTriangleIcon className="w-5 h-5" data-id="element-2488" />
          </div>
          <div data-id="element-2489">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-2490">
              Critical Load
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-2491">3</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-2492">
          <div className="p-3 bg-mavis-green/10 text-mavis-green rounded-none" data-id="element-2493">
            <ZapIcon className="w-5 h-5" data-id="element-2494" />
          </div>
          <div data-id="element-2495">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-2496">
              Active Transformers
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-2497">156</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-2498">
          <div className="p-3 bg-mavis-blue/10 text-mavis-blue rounded-none" data-id="element-2499">
            <ActivityIcon className="w-5 h-5" data-id="element-2500" />
          </div>
          <div data-id="element-2501">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-2502">
              Grid Frequency
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-2503">
              50.02 Hz
            </p>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center justify-between mb-4" data-id="element-2504">
        <h2 className="text-lg font-bold text-gray-900" data-id="element-2505">Substation Overview</h2>
        <div className="flex items-center bg-white border border-gray-200 rounded-none shadow-sm overflow-hidden" data-id="element-2506">
          <button onClick={() => setView('map')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${view === 'map' ? 'bg-mavis-teal text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2507">

            <MapIcon className="w-4 h-4" data-id="element-2508" />
            Map View
          </button>
          <button onClick={() => setView('list')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${view === 'list' ? 'bg-mavis-teal text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2509">

            <ListIcon className="w-4 h-4" data-id="element-2510" />
            List View
          </button>
        </div>
      </div>

      {/* Map View */}
      {view === 'map' && <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden mb-8 animate-in fade-in duration-300" data-id="element-2511">
          <div className="relative h-[500px]" data-id="element-2512">
            <MapContainer center={[-26.17, 28.28]} zoom={11} className="h-full w-full z-0" zoomControl={false} data-id="element-2513">

              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-2514" />


              {substationTableData.map((sub, i) => {
            const color = getStatusColor(sub.status);
            const radius = Math.max(10, Math.min(20, sub.load / 5));
            return <CircleMarker key={i} center={[sub.lat, sub.lng]} radius={radius} pathOptions={{
              color: '#ffffff',
              weight: 3,
              fillColor: color,
              fillOpacity: 0.85
            }} data-id="element-2515">

                    <Tooltip direction="top" offset={[0, -10]} opacity={1} data-id="element-2516">
                      <div className="flex items-start gap-2" data-id="element-2517">
                        <div className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{
                    backgroundColor: color
                  }} data-id="element-2518" />

                        <div data-id="element-2519">
                          <div className="font-bold text-gray-900" data-id="element-2520">
                            {sub.name}
                          </div>
                          <div className="text-gray-600 text-xs" data-id="element-2521">
                            Load: {sub.load}% · {sub.capacity}
                          </div>
                          <div className="text-[10px] font-semibold mt-0.5" style={{
                      color
                    }} data-id="element-2522">

                            {sub.status}
                          </div>
                        </div>
                      </div>
                    </Tooltip>

                    <Popup maxWidth={300} className="rounded-none" data-id="element-2523">
                      <div className="p-1 min-w-[260px]" data-id="element-2524">
                        <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-100" data-id="element-2525">
                          <div data-id="element-2526">
                            <h3 className="font-bold text-lg text-gray-900 m-0 leading-tight" data-id="element-2527">
                              {sub.name}
                            </h3>
                            <div className="text-xs text-gray-500 flex items-center gap-1 mt-1" data-id="element-2528">
                              <MapPinIcon className="w-3 h-3" data-id="element-2529" /> {sub.region}{' '}
                              Region
                            </div>
                          </div>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-none text-xs font-medium ${getStatusBadgeClass(sub.status)}`} data-id="element-2530">

                            <span className="w-2 h-2 rounded-full shrink-0" style={{
                        backgroundColor: color
                      }} data-id="element-2531" />

                            {sub.status}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 border border-gray-100 rounded-none" data-id="element-2532">
                          <div data-id="element-2533">
                            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-2534">
                              Capacity
                            </p>
                            <p className="font-mono font-bold text-mavis-teal text-lg leading-none" data-id="element-2535">
                              {sub.capacity}
                            </p>
                          </div>
                          <div data-id="element-2536">
                            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-2537">
                              Transformers
                            </p>
                            <p className="font-mono font-bold text-gray-900 text-lg leading-none" data-id="element-2538">
                              {sub.transformers}
                            </p>
                          </div>
                        </div>

                        <div className="mb-2" data-id="element-2539">
                          <div className="flex justify-between items-center mb-1" data-id="element-2540">
                            <p className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-2541">
                              Current Load
                            </p>
                            <p className={`font-mono font-bold text-lg ${getLoadColor(sub.load)}`} data-id="element-2542">

                              {sub.load}%
                            </p>
                          </div>
                          <div className="w-full bg-gray-200 h-3 rounded-none overflow-hidden" data-id="element-2543">
                            <div className={`h-full transition-all duration-500 ${getLoadBarColor(sub.load)}`} style={{
                        width: `${sub.load}%`
                      }} data-id="element-2544" />

                          </div>
                          <div className="flex justify-between mt-1" data-id="element-2545">
                            <span className="text-[9px] text-gray-400" data-id="element-2546">0%</span>
                            <span className="text-[9px] text-gray-400" data-id="element-2547">
                              50%
                            </span>
                            <span className="text-[9px] text-gray-400" data-id="element-2548">
                              100%
                            </span>
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>;
          })}
            </MapContainer>

            {/* Map Legend */}
            <div className="absolute bottom-4 left-4 z-[1000]" data-id="element-2549">
              <div className="bg-white/95 backdrop-blur-sm p-3 rounded-none border border-gray-200 shadow-md" data-id="element-2550">
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 border-b border-gray-100 pb-1" data-id="element-2551">
                  Status
                </h4>
                <div className="space-y-1.5" data-id="element-2552">
                  <div className="flex items-center gap-2" data-id="element-2553">
                    <div className="w-3 h-3 rounded-full bg-[#8DB43E] border border-white shadow-sm" data-id="element-2554"></div>
                    <span className="text-xs font-medium text-gray-700" data-id="element-2555">
                      Normal (&lt;70%)
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-2556">
                    <div className="w-3 h-3 rounded-full bg-[#E07020] border border-white shadow-sm" data-id="element-2557"></div>
                    <span className="text-xs font-medium text-gray-700" data-id="element-2558">
                      Warning (70-85%)
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-2559">
                    <div className="w-3 h-3 rounded-full bg-[#ef4444] border border-white shadow-sm" data-id="element-2560"></div>
                    <span className="text-xs font-medium text-gray-700" data-id="element-2561">
                      Critical (&gt;85%)
                    </span>
                  </div>
                </div>
                <p className="text-[9px] text-gray-400 mt-2 pt-1 border-t border-gray-100" data-id="element-2562">
                  Pin size = load level
                </p>
              </div>
            </div>
          </div>
        </div>}

      {/* List View: Chart + Table */}
      {view === 'list' && <div className="space-y-8 animate-in fade-in duration-300" data-id="element-2563">
          <div className="bg-white p-6 rounded-none shadow-sm border border-gray-200" data-id="element-2564">
            <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-2565">
              Substation Load (%)
            </h2>
            <div className="h-80" data-id="element-2566">
              <ResponsiveContainer width="100%" height="100%" data-id="element-2567">
                <BarChart data={substationChartData} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-2568">

                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-2569" />

                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} dy={10} data-id="element-2570" />

                  <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} data-id="element-2571" />

                  <RechartsTooltip cursor={{
                fill: '#f3f4f6'
              }} contentStyle={{
                borderRadius: '0',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} formatter={(value: number) => [`${value}%`, 'Load']} data-id="element-2572" />

                  <Bar dataKey="load" radius={[0, 0, 0, 0]} data-id="element-2573">
                    {substationChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.load > 80 ? '#E07020' : '#0D4F5C'} data-id="element-2574" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden" data-id="element-2575">
            <div className="p-5 border-b border-gray-200 bg-gray-50" data-id="element-2576">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-2577">
                Substation List
              </h2>
            </div>
            <div className="overflow-x-auto" data-id="element-2578">
              <table className="w-full text-left border-collapse" data-id="element-2579">
                <thead data-id="element-2580">
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider border-b border-gray-200" data-id="element-2581">
                    <th className="p-4 font-medium" data-id="element-2582">Name</th>
                    <th className="p-4 font-medium" data-id="element-2583">Region</th>
                    <th className="p-4 font-medium" data-id="element-2584">Capacity</th>
                    <th className="p-4 font-medium" data-id="element-2585">Transformers</th>
                    <th className="p-4 font-medium" data-id="element-2586">Current Load</th>
                    <th className="p-4 font-medium" data-id="element-2587">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-2588">
                  {substationTableData.map((sub, i) => <tr key={i} className="hover:bg-gray-50 transition-colors" data-id="element-2589">
                      <td className="p-4 text-sm font-medium text-gray-900" data-id="element-2590">
                        {sub.name}
                      </td>
                      <td className="p-4 text-sm text-gray-600" data-id="element-2591">
                        {sub.region}
                      </td>
                      <td className="p-4 text-sm font-mono text-gray-600" data-id="element-2592">
                        {sub.capacity}
                      </td>
                      <td className="p-4 text-sm font-mono text-gray-600" data-id="element-2593">
                        {sub.transformers}
                      </td>
                      <td className="p-4" data-id="element-2594">
                        <div className="flex items-center gap-2" data-id="element-2595">
                          <div className="w-16 bg-gray-200 h-2 rounded-none overflow-hidden" data-id="element-2596">
                            <div className={`h-full ${getLoadBarColor(sub.load)}`} style={{
                        width: `${sub.load}%`
                      }} data-id="element-2597" />

                          </div>
                          <span className={`text-sm font-mono font-bold ${getLoadColor(sub.load)}`} data-id="element-2598">

                            {sub.load}%
                          </span>
                        </div>
                      </td>
                      <td className="p-4" data-id="element-2599">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-none text-xs font-medium ${getStatusBadgeClass(sub.status)}`} data-id="element-2600">

                          {sub.status}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>}
    </div>;
}